/**
 * The toast after an adopt or a Connect succeeds (ACT2-1130).
 *
 * The server dialled the account before it bound it, and ran the same `_rudderstack` write probe Test
 * connection runs. That probe never blocks the adopt, so a missing grant arrives beside the success:
 * a warning toast whose title is the success and whose description says what is missing and where to
 * fix it. The words are `retlPrivilegeAdoptWarning`'s, from the backend module that owns the probe —
 * not Test's own `retlPrivilegeWarning`, which points at setup SQL "on this page" a toast does not have.
 *
 * The same check also counts the tables the credential can see outside `_rudderstack`. None means
 * the account connects and reads none of the customer's data; that warning comes first, since it
 * stops every query from finding anything, and the two share one description when both apply.
 */
import type { RouterClient } from '@orpc/server';
import { readAccessAdoptWarning, retlPrivilegeAdoptWarning } from '@server/retl-schema';
import type { AppRouter } from '@server/rpc';
import { toast } from 'sonner';

type WarehouseProcedures = RouterClient<AppRouter>['settings']['warehouses'];
type AdoptOutput = Awaited<ReturnType<WarehouseProcedures['adoptAccount']>>;

/**
 * The verdict both adopt procedures return, derived from the router. Partial because a server that
 * predates ACT2-1130 (a rolling deploy) sends none.
 */
export type AdoptedResult = Partial<Pick<AdoptOutput, 'retlSchema' | 'readAccess'>>;

export function toastAdopted(success: string, result: AdoptedResult): void {
  const warnings = [
    result.readAccess ? readAccessAdoptWarning(result.readAccess) : null,
    result.retlSchema ? retlPrivilegeAdoptWarning(result.retlSchema) : null,
  ].filter((warning): warning is string => warning !== null);
  if (warnings.length > 0) toast.warning(success, { description: warnings.join(' ') });
  else toast.success(success);
}
